import { useCallback, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { Checkbox, Typography, useAlert } from "matsuri-ui"
import styled from "@emotion/styled"
import { usePlacementIncompleteCtx } from "./providers/placementIncomplete"
import { INVENTORY_LINKED_PHOTO_TOUR_IDS } from "@/constants/inventoryLinkedPhotoTourIds"
import { ConfirmLayout } from "@/templates/ConfirmLayout"
import { assertIsDefined } from "@/utils/assetIsDefined"
import { genOperationReportPagePath } from "@/routes/protected"
import { requestReportOperation, useOperation } from "@/hooks/useOperaion"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { BackToDetailConfirmDialog } from "@/components/Dialog/BackToDetailConfirmDialog"
import { useConfirmDialog } from "@/hooks/useConfirmDialog"
import { useNavigateBackToDetail } from "@/hooks/useNavigateBackToDetail"
import { useTranslation } from "@/hooks/useTranslation"
import { theme } from "@/theme"

const PlacementIncompleteRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border: 1px solid ${theme.palette.intentions.cleanerGray.main};
    border-radius: 8px;
    margin: 16px 16px 0;
`

// 「完了できません」ボタンも表示するツアーIDのリスト
const OPTIONAL_INCOMPLETE_TOUR_IDS =
    process.env.NODE_ENV !== "production"
        ? // 開発環境
          [
              "58ac95d6-15d1-47bf-b470-2317ac9b9ef3",
              "797bb292-ca23-47d2-9d30-eba6ef07b72b",
              "79cc7c2d-3467-4f85-b883-5c2c8be9cf0c",
              "d1fb3f1e-c1dd-4112-9d26-de54d0e9ef42",
              "e6913c70-9956-4eab-9510-d1821e3f3eb3",
              "f0aa98cc-bd91-4edc-a98e-0176afdcc6c3",
              "f7045461-2bcd-40ac-a126-f958f303a89a"
          ]
        : // 本番環境
          [
              "d80c892e-4bf8-4adc-94d1-3a656a4e155c",
              "b20a076a-ca6c-498e-b462-87b1ada13457",
              "46edf6b3-82f9-444d-9b75-92bf2d10b896",
              "37dc717e-a0c2-4f64-867d-6a5ff12efd1b",
              "a2e56c71-a743-40f3-a73f-3445639ef559",
              "9711e15a-ffc0-43d8-94be-029e91d7cf1a",
              "71017dc9-e28b-4c16-90ce-30a172024939"
          ]

interface TroublesConfirmPageProps {
    title: string
}

export const TroublesConfirmPage = ({ title }: TroublesConfirmPageProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { operationId } = useParams<"operationId">()
    assertIsDefined(operationId)

    const { token } = useAuth()
    const { throwAlert } = useAlert()
    const { data: operation } = useOperation(token, operationId)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { placementIncomplete, setPlacementIncomplete } =
        usePlacementIncompleteCtx()

    // 連動対象 photo_tour のツアーのみ「設置は未完了」オプションを表示
    const showPlacementIncompleteOption =
        Boolean(operation?.photoTourId) &&
        INVENTORY_LINKED_PHOTO_TOUR_IDS.includes(operation?.photoTourId ?? "")

    // 既にreported済みの場合はfinishページにリダイレクト（ブラウザ戻るボタン対策）
    useEffect(() => {
        if (operation?.status === "reported") {
            navigate(genOperationReportPagePath(operationId, "finish"), {
                replace: true
            })
        }
    }, [operation?.status, navigate, operationId])

    // photoTourIdが特定のIDの場合は「完了できません」ボタンも表示
    const showIncompleteOption =
        operation?.photoTourId &&
        OPTIONAL_INCOMPLETE_TOUR_IDS.includes(operation.photoTourId)

    // ツアー詳細画面に戻る確認モーダル
    const {
        confirmBackOpen,
        openConfirmBack,
        closeConfirmBack,
        goBackToDetail
    } = useNavigateBackToDetail("operation", operationId)

    // 報告終了の確認モーダル
    const {
        open: confirmFinishOpen,
        openDialog: openConfirmFinish,
        closeDialog: closeConfirmFinish
    } = useConfirmDialog()

    // 完了できませんの確認モーダル
    const {
        open: confirmIncompleteOpen,
        openDialog: openConfirmIncomplete,
        closeDialog: closeConfirmIncomplete
    } = useConfirmDialog()

    const gotoTroubles = useCallback(() => {
        navigate(genOperationReportPagePath(operationId, "troubles"))
    }, [operationId, navigate])

    const submitReport = useCallback(
        async (
            onClose: () => void,
            body?: Parameters<typeof requestReportOperation>[2]
        ) => {
            setIsSubmitting(true)
            try {
                const { error } = await requestReportOperation(
                    token,
                    operationId,
                    body
                )
                if (error) throw error
                onClose()
                navigate(genOperationReportPagePath(operationId, "finish"))
            } catch (e) {
                throwAlert(e instanceof Error ? e : new Error(String(e)), {
                    errorMessage: t("報告の送信に失敗しました")
                })
            } finally {
                setIsSubmitting(false)
            }
        },
        [operationId, navigate, token, throwAlert, t]
    )

    const finishAndGotoFinish = useCallback(
        () =>
            submitReport(
                closeConfirmFinish,
                placementIncomplete ? { placementIncomplete: true } : undefined
            ),
        [submitReport, closeConfirmFinish, placementIncomplete]
    )

    const reportIncompleteAndGotoFinish = useCallback(
        () =>
            submitReport(closeConfirmIncomplete, {
                quality_status: "incomplete",
                ...(placementIncomplete && { placementIncomplete: true })
            }),
        [submitReport, closeConfirmIncomplete, placementIncomplete]
    )

    return (
        <>
            <ConfirmLayout
                headerTitle={t("トラブル報告")}
                onNext={openConfirmBack}
                title={title}
                primaryButtonLabel={t("トラブルを報告する")}
                cancelButtonLabel={t("清掃報告終了")}
                tertiaryButtonLabel={
                    showIncompleteOption ? t("完了できません") : undefined
                }
                tertiaryErrorColor={true}
                next={gotoTroubles}
                cancel={openConfirmFinish}
                tertiary={
                    showIncompleteOption ? openConfirmIncomplete : undefined
                }
            >
                {showPlacementIncompleteOption && (
                    <PlacementIncompleteRow>
                        <Checkbox
                            checked={placementIncomplete}
                            onChange={() => setPlacementIncomplete(v => !v)}
                            label={
                                <Typography variant="body">
                                    {t("設置は未完了")}
                                </Typography>
                            }
                        />
                    </PlacementIncompleteRow>
                )}
            </ConfirmLayout>
            {/* 報告終了の確認モーダル */}
            <ConfirmDialog
                open={confirmFinishOpen}
                title={t("報告を終了する")}
                message={t(
                    "報告を終了すると、ツアー詳細画面に戻れなくなるため、鍵情報を確認することができなくなります。"
                )}
                onConfirm={finishAndGotoFinish}
                onCancel={closeConfirmFinish}
                cancelLabel={t("戻る")}
                confirmLabel={t("終了")}
                confirmLoading={isSubmitting}
                cancelDisabled={isSubmitting}
            />
            {/* ツアー詳細画面に戻る確認モーダル */}
            <BackToDetailConfirmDialog
                type="operation"
                open={confirmBackOpen}
                onClose={closeConfirmBack}
                onConfirm={goBackToDetail}
            />
            {/* 完了できませんの確認モーダル */}
            <ConfirmDialog
                open={confirmIncompleteOpen}
                message={t(
                    "問題があり完了できなかったことを報告します。よろしいですか？"
                )}
                onConfirm={reportIncompleteAndGotoFinish}
                onCancel={closeConfirmIncomplete}
                confirmLoading={isSubmitting}
                cancelDisabled={isSubmitting}
            />
        </>
    )
}
